import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    MenuItem,
    Select,
    FormControl,
    Button,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useState } from 'react';
import { queryWasteMeasurementsForDataset } from 'src/schema';
import LoadingIndicator from './LoadingIndicator';
import DataSummaryModal from './DataSummaryModal';
import { getOneLink } from 'src/utils';
import { convertAndFilterMeasurements } from 'src/wasteConversion';

interface Props {
    datasetId: string;
    eventId: string;
}

interface MaterialSummary {
    materialId: string;
    materialName: string;
    total: number;
    units: string;
}

interface CategorySummary {
    categoryId: string;
    categoryName: string;
    materials: MaterialSummary[];
    categoryTotal: number;
}

const DISPLAY_UNITS = {
    mass: ['lb', 'kg', 'g', 'oz', 't', 'mt'] as const,
    volume: ['gal', 'l', 'ml', 'fl-oz', 'm3', 'ft3', 'yd3'] as const,
};

type MassUnit = (typeof DISPLAY_UNITS.mass)[number];
type VolumeUnit = (typeof DISPLAY_UNITS.volume)[number];
type DisplayUnit = MassUnit | VolumeUnit;

const EMPTY_ARRAY: never[] = [];

export default function DataSummary(props: Props) {
    const { datasetId, eventId } = props;
    const { data, isLoading, error } = queryWasteMeasurementsForDataset(datasetId);
    const [expanded, setExpanded] = useState(true);
    const [displayUnit, setDisplayUnit] = useState<DisplayUnit>('lb');
    const [modalOpen, setModalOpen] = useState(false);

    const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded);
    };

    const wasteMeasurements = data?.wasteMeasurements ?? EMPTY_ARRAY;

    // Convert all measurements to display unit (handles custom units and volume↔mass via density)
    const convertedMeasurements = useMemo(
        () =>
            convertAndFilterMeasurements(wasteMeasurements, {
                targetUnit: displayUnit,
                itemIds: [],
                datasetIds: [],
            }),
        [wasteMeasurements, displayUnit]
    );

    const measurementType = useMemo(() => {
        if (convertedMeasurements.length === 0) return 'mass';
        const firstUnit = convertedMeasurements[0]?.units;
        if (!firstUnit) return 'mass';
        return DISPLAY_UNITS.mass.includes(firstUnit as MassUnit) ? 'mass' : 'volume';
    }, [convertedMeasurements]);

    const availableUnits = measurementType === 'mass' ? DISPLAY_UNITS.mass : DISPLAY_UNITS.volume;

    const categorySummaries = useMemo(() => {
        const categoryMap = new Map<string, CategorySummary>();

        convertedMeasurements.forEach(measurement => {
            const material = getOneLink(measurement.material);
            const category = getOneLink(material?.category);
            if (!material || !category || !measurement.quantity) return;

            const categoryId = category.id;
            const categoryName = category.name as string;
            const materialId = material.id;
            const materialName = material.name as string;
            const convertedQuantity = measurement.quantity as number;

            if (!categoryMap.has(categoryId)) {
                categoryMap.set(categoryId, {
                    categoryId,
                    categoryName,
                    materials: [],
                    categoryTotal: 0,
                });
            }
            const categorySummary = categoryMap.get(categoryId);
            if (!categorySummary) return;
            const existingMaterialSummary = categorySummary.materials.find(m => m.materialId === materialId);

            if (!existingMaterialSummary) {
                const newMaterialSummary: MaterialSummary = {
                    materialId,
                    materialName,
                    total: convertedQuantity,
                    units: displayUnit,
                };
                categorySummary.materials.push(newMaterialSummary);
            } else {
                existingMaterialSummary.total += convertedQuantity;
            }

            categorySummary.categoryTotal += convertedQuantity;
        });

        const result: CategorySummary[] = [];
        for (const v of Array.from(categoryMap.values())) result.push(v);
        return result;
    }, [convertedMeasurements, displayUnit]);

    const grandTotal = useMemo(() => {
        return categorySummaries.reduce((sum, cat) => sum + cat.categoryTotal, 0);
    }, [categorySummaries]);

    if (isLoading) {
        return <LoadingIndicator message="Loading summary..." />;
    }

    if (error) {
        return <Typography color="error">Error loading summary data</Typography>;
    }

    if (wasteMeasurements.length === 0) {
        return null;
    }

    return (
        <>
            <DataSummaryModal
                datasetId={datasetId}
                eventId={eventId}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                displayUnit={displayUnit}
            />
            <Accordion
                expanded={expanded}
                onChange={handleAccordionChange}
                elevation={0}
                sx={{
                    mt: 2,
                    mb: 2,
                    width: '80%',
                    mx: 'auto',
                    '&:before': {
                        display: 'none',
                    },
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <AccordionSummary
                    expandIcon={null}
                    aria-controls="summary-content"
                    id="summary-header"
                    sx={{
                        minHeight: 56,
                        '& .MuiAccordionSummary-content': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            my: 1.5,
                        },
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                setExpanded(!expanded);
                            }}
                            sx={{
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>
                            Summary
                        </Typography>
                    </Box>
                    <Button
                        variant="text"
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            setModalOpen(true);
                        }}
                        sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}
                    >
                        Visualize
                    </Button>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
                        <FormControl size="small" sx={{ minWidth: 60 }}>
                            <Select
                                value={displayUnit}
                                onChange={e => setDisplayUnit(e.target.value as DisplayUnit)}
                                sx={{ fontSize: '0.875rem' }}
                            >
                                {availableUnits.map(unit => (
                                    <MenuItem key={unit} value={unit}>
                                        {unit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Material</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categorySummaries.map(category => (
                                    <TableRow key={category.categoryId}>
                                        <TableCell>{category.categoryName}</TableCell>
                                        <TableCell>—</TableCell>
                                        <TableCell align="right">{category.categoryTotal.toFixed(1)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                                    <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                                        Grand Total:
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {grandTotal.toFixed(1)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        </>
    );
}
